import styles from './DiffPreview.module.scss'
import sharedStyles from '@/pages/app/AnalyticsPortal/styles.module.scss'

import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import { FIELD_LABELS } from '@/pages/app/AnalyticsPortal/constants'
import type { DiffChange, DiffChangeType } from '@/pages/app/AnalyticsPortal/types'
import { getButtonTitle } from '@/pages/app/AnalyticsPortal/utils'

interface DiffPreviewProps {
  mode: 'save' | 'publish'
  title: string
  eventChanges: DiffChange[]
  propertyChanges: DiffChange[]
  isLoading?: boolean
  onConfirm: () => void
  onCancel: () => void
}

const TYPE_STYLES: Record<DiffChangeType, string> = {
  new: styles.new,
  updated: styles.updated,
  deprecated: styles.deprecated,
  renamed: styles.updated,
}

const DiffPreview = ({
  mode,
  title,
  eventChanges,
  propertyChanges,
  isLoading,
  onConfirm,
  onCancel,
}: DiffPreviewProps) => {
  const total = eventChanges.length + propertyChanges.length
  const allChanges = [...propertyChanges, ...eventChanges]

  return (
    <DialogWrapper
      isOpen={true}
      title={title}
      subtitle={`${total} change${total !== 1 ? 's' : ''}`}
      close={onCancel}
      minWidth={480}
    >
      <div className={styles.dialogContent}>
        <div className={styles.diffContainer}>
          {allChanges.map((c) => (
            <DiffItem key={c.id} change={c} />
          ))}
        </div>
        <div className={sharedStyles.dialogFooter}>
          <Button title="Cancel" onClick={onCancel} type="light" />
          <Button
            title={getButtonTitle(
              isLoading ?? false,
              mode === 'save' ? 'Saving...' : 'Publishing...',
              mode === 'save' ? 'Save Changes' : 'Publish Changes'
            )}
            onClick={onConfirm}
            type="dark"
            isDisabled={isLoading}
            isLoading={isLoading}
          />
        </div>
      </div>
    </DialogWrapper>
  )
}

const DiffItem = ({ change }: { change: DiffChange }) => {
  const hasItemDetails = change.description || change.trigger || change.platforms?.length || change.lifecycle
  const hasFieldDetails = change.fields && change.fields.length > 0

  return (
    <div className={styles.diffItem}>
      <div className={styles.diffItemHeader}>
        <div className={styles.diffItemNameWrapper}>
          <span className={styles.diffItemName}>{change.name}</span>
        </div>
        <span className={`${styles.diffItemType} ${TYPE_STYLES[change.type]}`}>{change.type}</span>
      </div>
      {(hasItemDetails || hasFieldDetails) && (
        <div className={styles.diffItemBody}>
          {hasFieldDetails ? (
            <div className={styles.diffFields}>
              {change.fields!.map((f, i) => (
                <div key={i} className={styles.diffField}>
                  <span className={styles.diffFieldName}>{FIELD_LABELS[f.field] || f.field}</span>
                  <div className={styles.diffFieldValues}>
                    <span className={styles.diffOldValue}>{f.oldVal || '(none)'}</span>
                    <span className={styles.diffNewValue}>{f.newVal || '(none)'}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : hasItemDetails ? (
            <div className={styles.savePreviewFields}>
              {change.description && (
                <div className={styles.savePreviewField}>
                  <span className={styles.savePreviewLabel}>Description:</span>
                  <span>{change.description}</span>
                </div>
              )}
              {change.trigger && (
                <div className={styles.savePreviewField}>
                  <span className={styles.savePreviewLabel}>Trigger:</span>
                  <span>{change.trigger}</span>
                </div>
              )}
              {change.platforms?.length && (
                <div className={styles.savePreviewField}>
                  <span className={styles.savePreviewLabel}>Platforms:</span>
                  <span>{change.platforms.join(', ')}</span>
                </div>
              )}
              {change.lifecycle && (
                <div className={styles.savePreviewField}>
                  <span className={styles.savePreviewLabel}>Lifecycle:</span>
                  <span>{change.lifecycle}</span>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

export default DiffPreview
